/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { BaseComponent } from '../base.component';
import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ContentInjectionService } from '../../services';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { environment } from '../../../../environments/environment';

@Component({
    selector: 'app-tooltip',
    styleUrls: ['./tooltip.component.scss'],
    template: `
        <div #tooltipTrigger class="tooltip-wrapper">
            <div
                *ngIf="!triggerTemplate"
                [innerHTML]="trigger"
                [style.visibility]="triggerTransparent ? 'hidden' : 'visible'"
                class="tooltip-trigger">
            </div>
            <ng-container *ngIf="triggerTemplate" [ngTemplateOutlet]="triggerTemplate"></ng-container>
        </div>
        <ng-template #tooltipContainer>
            <div id="tooltip-container">
                <div
                    #tooltip class="tooltip {{ tooltipPlacement }}"
                    [class.force-right]="forceRight"
                    role="tooltip"
                    [style.width.px]="width">
                    <div *ngIf=!innerStyling class="tooltip-arrow"></div>
                    <div
                        class="tooltip-inner" [style.background]="backgroundColor" [style]="innerStyling"
                        [style.width.px]="width"
                        [style.height.px]="height">
                        <h5 *ngIf="title" class="tooltip-title">{{ title }}</h5>
                        <ng-container *ngIf="content; else html">
                            <span class="tooltip-body" [style.color]="textColor" [innerHTML]="content"></span>
                        </ng-container>
                        <ng-template #html>
                            <ng-content></ng-content>
                        </ng-template>
                    </div>
                </div>
            </div>
        </ng-template>
    `,
    animations: [
        trigger('showHide', [
            state('show',
                style({
                    opacity: 1,
                    visibility: 'visible'
                })
            ),
            state('hide',
                style({
                    opacity: 0,
                    visibility: 'hidden'
                })
            ),
            transition('hide => show',
                animate('{{animation}}ms ease-in'), { params: { animation: 0 } }
            ),
            transition('show => hide',
                animate('{{animation}}ms {{delay}}ms ease-out'), { params: { animation: 0, delay: 0 } }
            )
        ])
    ]
})
export class TooltipComponent extends BaseComponent implements OnInit, OnDestroy {
    /**
     * Reference to the text that trigger the tooltip.
     */
    @ViewChild('tooltipTrigger', { static: true })
    public triggerElement: ElementRef;

    /**
     * Reference to the tooltip container.
     */
    @ViewChild('tooltip', { static: true })
    public tooltipElement: ElementRef;

    /**
     * Container of the tooltip to show.
     */
    @ViewChild('tooltipContainer', { static: true })
    public tooltipContainer: TemplateRef<any>;

    /**
     * The element that triggers the tooltip to be displayed
     */
    @Input() public trigger: HTMLElement | string;

    /**
     * Pass in a template as a trigger.
     */
    @Input() public triggerTemplate: TemplateRef<any>;

    /**
     * Enabled the trigger text to be hidden if additional embedded styling is required.
     */
    @Input() public triggerTransparent: boolean = false;

    /**
     * Optional input for tooltip title.
     */
    @Input() public title: string;

    /**
     * Optional input, if not set the component will use ng-content.
     */
    @Input() public content: string;

    /**
     * Placement of the tooltip relative to the trigger.
     */
    @Input() public externalPlacement: 'top' | 'bottom';

    /**
     * Delay between mouse leaving the trigger and the tooltip being hidden.
     */
    @Input() public delay: number = 500;

    /**
     * Delay after mouse covers the trigger and the tooltip being shown.
     */
    @Input() public startDelay: number = 200;

    /**
     * Animation time, if set to zero no animation will occur (seems to be a bug with delay, set to 0.1 for no animation).
     */
    @Input() public animation: number = 150;

    /**
     * Set the height of the tooltip.
     */
    @Input() public height: number | string | 'auto' = 'auto';

    /**
     * Set the width of the tooltip.
     */
    @Input() public width: number = 250;

    /**
     * Set the style of tooltip inner.
     */
    @Input() public innerStyling: any = null;

    /**
     * Force the tooltip to sit right of the trigger.
     */
    @Input() public forceRight: boolean = false;

    /**
     * Set tooltip inner and caret colour.
     */
    @Input() public textColor: string = '#fff';

    /**
     * Set tooltip inner and caret colour.
     */
    @Input() public backgroundColor: string = '#5c61d4';

    /**
     * Help with styling (dev).
     */
    @Input() public forceShow: boolean = false;

    /**
     * Placement state store.
     */
    public tooltipPlacement: 'top' | 'bottom';

    /**
     * ID to store the timeout.
     * @private
     */
    private timeoutID: any;

    constructor(private contentInjectionService: ContentInjectionService) {
        super();
    }

    ngOnInit(): void {
        if (!environment.production && this.forceShow) {
            const overlayContentState = this.createOverlayContentState();
            this.contentInjectionService.openEmitter.next(overlayContentState);
        }
    }

    ngOnDestroy(): void {
        if (this.timeoutID) {
            clearTimeout(this.timeoutID);
        }
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }

    /**
     * Triggered on focus on mouse enter and displays the tooltip.
     */
    @HostListener('focusin')
    @HostListener('mouseenter')
    public show(): void {
        // To delay tooltip from showing unless hovered for milliseconds set in startDelay.
        this.timeoutID = setTimeout(() => {
            if (this.timeoutID) {
                this.openTooltip();
            }
        }, this.startDelay);
        this.setTooltipPlacement(this.getElementDimensions(this.triggerElement));
    }

    /**
     * When focus is lost or the mouse leaves the tooltip is hidden.
     */
    @HostListener('focusout')
    @HostListener('mouseleave')
    public hide(): void {
        clearTimeout(this.timeoutID);
        const toggleTimeout = setTimeout(() => {
            // Clear timeout on mouseleave to prevent tooltip from being triggered.
            if ('mouseleave' && !this.contentInjectionService.contentHovered) {
                this.contentInjectionService.closeEmitter.next(true);
            }
            clearTimeout(toggleTimeout);
        }, 250);
    }

    /**
     * If the tooltip is clicked cancel the mouseEnter/focusIn animation.
     */
    @HostListener('click')
    click(): void {
        clearTimeout(this.timeoutID);
        this.timeoutID = null;
    }

    /**
     * Open the tooltip.
     * @private
     */
    private openTooltip(): void {
        const overlayContentState: object = this.createOverlayContentState();
        this.contentInjectionService.openEmitter.next(overlayContentState);
    }

    /**
     * Create overlay content state.
     * @private
     */
    private createOverlayContentState(): any {
        const rect: any = this.triggerElement.nativeElement.getBoundingClientRect();
        const left: any = this.forceRight ? (rect.left + rect.width + 10) : (rect.left - this.width - 10);

        return {
            id: 'tooltip',
            open: true,
            template: this.tooltipContainer,
            containerWidth: this.width,
            top: rect.top + (rect.height * 0.5),
            bottom: rect.bottom - (rect.height * 0.5),
            left: left,
            event: rect,
            isEmbedded: false,
            showCaret: true,
            overlay: false,
            overlaySelector: 'tooltip-container',
            fade: true,
            forceRight: this.forceRight,
            backgroundColor: this.backgroundColor,
        };
    }

    /**
     * Get elements dimensions from the DOM.
     * @param element
     * @private
     */
    private getElementDimensions(element: any) {
        const elem = element.nativeElement.children[0];

        return {
            height: elem.offsetHeight,
            width: elem.offsetWidth
        };
    }

    /**
     * Calculate the placement if in modal or not.
     * @param tooltipDimensions
     * @private
     */
    private setTooltipPlacement(tooltipDimensions: any): void {
        if (this.externalPlacement) {
            this.tooltipPlacement = this.externalPlacement;
        } else {
            const parentIsModal = this.triggerElement.nativeElement.closest('.modal-body');
            this.tooltipPlacement = parentIsModal
                ? this.calculatePlacementAgainstModal(tooltipDimensions)
                : this.calculatePlacementAgainstWindow(tooltipDimensions);
        }
    }

    /**
     * Checks the placement of the tooltip against a modal and returns an array of how the tooltip should be displayed.
     * @param tooltipDimensions
     * @private
     */
    private calculatePlacementAgainstModal(tooltipDimensions: any): 'top' | 'bottom' {
        let placement: 'top' | 'bottom' = 'bottom';
        const modal = this.triggerElement.nativeElement.closest('.modal-body');
        const modalBodyRecs = modal.getBoundingClientRect();
        const triggerRecs = this.triggerElement.nativeElement.getBoundingClientRect();

        if (modalBodyRecs.bottom - triggerRecs.bottom < tooltipDimensions.height + 10) {
            placement = 'top';
        }

        if (triggerRecs.top - modalBodyRecs.top < tooltipDimensions.height + 10) {
            placement = 'bottom';
        }

        return placement;
    }

    /**
     * Checks the placement of the tooltip against the window edge and returns an array of how the tooltip should be displayed.
     * @param tooltipDimensions
     * @private
     */
    private calculatePlacementAgainstWindow(tooltipDimensions: any): 'top' | 'bottom' {
        let placement: 'top' | 'bottom' | 'left' | 'right' = 'bottom';
        const triggerRecs = this.triggerElement.nativeElement.getBoundingClientRect();
        if (triggerRecs.y - tooltipDimensions.height < 0) {
            placement = 'bottom';
        }

        if (triggerRecs.y + tooltipDimensions.height + 80 > window.innerHeight) {
            placement = 'top';
        }

        return placement;
    }
}
